import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectBuchungen, selectKategorien } from 'modules/finanzen/features/finanzenSlice';
import {
  barvermoegenVon,
  buchungenFuerOrt,
  summen as buchungssummen,
  istEinnahme,
  betragVon,
  heute,
} from 'modules/finanzen/features/berechnung';
import { kategorieIcon, TYP_AUSGABE } from 'modules/finanzen/features/konstanten';
import {
  selectInvestitionen,
  selectDepotWerte,
  selectDokumente,
} from 'modules/investitionen/features/investitionenSlice';
import { bereichSummen, positionInvestiert, positionWert } from 'modules/investitionen/features/berechnung';
import { formatKurs, formatDatum } from 'core/utils/format';
import BackToOverview from 'core/components/BackToOverview';
import GesichertGate from 'modules/finanzen/components/GesichertGate';
import GewinnBadge from 'modules/investitionen/components/GewinnBadge';
import InvestitionForm from 'modules/investitionen/components/InvestitionForm';
import PositionForm from 'modules/investitionen/components/PositionForm';
import DokumentePanel from 'modules/investitionen/components/DokumentePanel';
import BuchungForm from 'modules/finanzen/components/BuchungForm';

/** Detailseite eines Investitionsorts: Barvermögen und Investitionen (Positionen). */
export default function OrtDetailPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-12">
      <div className="mb-6">
        <BackToOverview />
      </div>
      <GesichertGate>
        <Detail />
      </GesichertGate>
    </div>
  );
}

/** Entsperrte Detailansicht; sucht den Ort anhand der Route-UUID. */
function Detail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const orte = useSelector(selectInvestitionen);
  const buchungen = useSelector(selectBuchungen);
  const depotWerte = useSelector(selectDepotWerte);
  const alleDokumente = useSelector(selectDokumente);
  const [ortEditor, setOrtEditor] = useState(false);
  const [positionEditor, setPositionEditor] = useState(null);
  const [buchungEditor, setBuchungEditor] = useState(null);

  const ort = orte.find((o) => o.uuid === uuid);
  const dokumente = useMemo(() => alleDokumente.filter((d) => d.investitionUuid === uuid), [alleDokumente, uuid]);
  const ortsBuchungen = useMemo(
    () => buchungenFuerOrt(buchungen, uuid).slice().sort((a, b) => String(b.datum).localeCompare(String(a.datum))),
    [buchungen, uuid]
  );

  if (!ort) {
    return (
      <p className="rounded-2xl border border-dashed border-line bg-paper-soft p-8 text-center text-ink-muted">
        Ort nicht gefunden. <Link to="/projekte/finanzen" className="text-brand-600 hover:underline">Zur Übersicht</Link>
      </p>
    );
  }

  const bar = barvermoegenVon(ort, buchungen);
  const saldo = buchungssummen(ortsBuchungen).saldo;
  const { investiert, wert, gv, gvProz } = bereichSummen(ort, depotWerte);
  const positionen = ort.positionen || [];

  const neuePosition = () =>
    setPositionEditor({ id: crypto.randomUUID(), bezeichnung: '', investiert: '', aktuellerWert: '', isin: '', datum: '', notiz: '' });
  const neueBuchung = () => setBuchungEditor({ typ: TYP_AUSGABE, betrag: '', datum: heute(), ort: uuid });

  return (
    <>
      <Kopf ort={ort} onBearbeiten={() => setOrtEditor(true)} />

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Kachel label="Barvermögen" wert={formatKurs(bar)} />
        <Kachel label="Investiert (Wert)" wert={formatKurs(wert)} />
        <Kachel label="Gesamt" wert={formatKurs(bar + wert)} />
      </div>

      {/* Abschnitt 1: Barvermögen */}
      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-ink">Barvermögen</h2>
          <button
            type="button"
            onClick={neueBuchung}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-500"
          >
            + Buchung
          </button>
        </div>
        <div className="mb-4 grid grid-cols-3 gap-3 rounded-2xl border border-line bg-paper-soft p-4 text-center text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-muted">Anfangsbestand</p>
            <p className="mt-1 font-serif text-lg font-semibold text-ink tabular-nums">{formatKurs(ort.anfangsbestand || 0)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-muted">Buchungen (Saldo)</p>
            <p className={`mt-1 font-serif text-lg font-semibold tabular-nums ${saldo >= 0 ? 'text-gain' : 'text-loss'}`}>
              {saldo >= 0 ? '+' : '−'} {formatKurs(Math.abs(saldo))}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-muted">Barvermögen</p>
            <p className="mt-1 font-serif text-lg font-semibold text-ink tabular-nums">{formatKurs(bar)}</p>
          </div>
        </div>
        <BuchungsListe buchungen={ortsBuchungen} onBearbeiten={setBuchungEditor} />
      </section>

      {/* Abschnitt 2: Investiert */}
      <section className="mb-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-ink">
            Investiert
            {investiert > 0 && <span className="ml-3 align-middle text-sm"><GewinnBadge gv={gv} gvProz={gvProz} waehrung={ort.waehrung} /></span>}
          </h2>
          <button
            type="button"
            onClick={neuePosition}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-500"
          >
            + Position
          </button>
        </div>
        <PositionenTabelle
          positionen={positionen}
          waehrung={ort.waehrung}
          depotWerte={depotWerte}
          onBearbeiten={(p) => setPositionEditor({ ...p, investiert: p.investiert ?? '', aktuellerWert: p.aktuellerWert ?? '', isin: p.isin ?? '', datum: p.datum ?? '' })}
        />
      </section>

      <section>
        <h2 className="mb-3 font-serif text-xl font-semibold text-ink">Dokumente (Steuer)</h2>
        <DokumentePanel investitionUuid={ort.uuid} dokumente={dokumente} />
      </section>

      {ortEditor && (
        <InvestitionForm
          investition={ort}
          onClose={() => setOrtEditor(false)}
          onGeloescht={() => navigate('/projekte/finanzen')}
        />
      )}
      {positionEditor && (
        <PositionForm
          bereich={ort}
          position={positionEditor}
          depotWerte={depotWerte}
          onClose={() => setPositionEditor(null)}
        />
      )}
      {buchungEditor && <BuchungForm buchung={buchungEditor} onClose={() => setBuchungEditor(null)} />}
    </>
  );
}

/** Kopfzeile mit Ortsname, Kategorie, Link und Bearbeiten-Button. */
function Kopf({ ort, onBearbeiten }) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-ink">{ort.institut}</h1>
        <p className="mt-1 text-sm text-ink-muted">{ort.kategorie || 'Ohne Kategorie'} · {ort.waehrung}</p>
        {ort.notiz && <p className="mt-2 max-w-xl text-sm text-ink-soft">{ort.notiz}</p>}
        {ort.link && (
          <a href={ort.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-sm text-brand-600 hover:underline">
            Öffnen ↗
          </a>
        )}
      </div>
      <button
        type="button"
        onClick={onBearbeiten}
        className="rounded-lg border border-line px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
      >
        Ort bearbeiten
      </button>
    </header>
  );
}

/** Eine Kennzahl-Kachel. */
function Kachel({ label, wert }) {
  return (
    <div className="rounded-2xl border border-line bg-paper-soft p-5">
      <p className="text-xs uppercase tracking-wide text-ink-muted">{label}</p>
      <p className="mt-1 font-serif text-xl font-semibold text-ink">{wert}</p>
    </div>
  );
}

/** Liste der diesem Ort zugeordneten Buchungen; klickbar zum Bearbeiten. */
function BuchungsListe({ buchungen, onBearbeiten }) {
  const eigeneKategorien = useSelector(selectKategorien);
  if (buchungen.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-line bg-paper-soft p-6 text-center text-sm text-ink-muted">
        Noch keine Buchungen für diesen Ort. Erfasse Ein- und Auszahlungen mit „+ Buchung“.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-paper">
      {buchungen.map((b) => {
        const einnahme = istEinnahme(b);
        const ton = einnahme ? 'text-gain' : 'text-loss';
        return (
          <li key={b.uuid}>
            <button
              type="button"
              onClick={() => onBearbeiten(b)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-ink/5"
            >
              <span className="text-xl" aria-hidden="true">{kategorieIcon(b.typ, b.kategorie, eigeneKategorien)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-ink">
                  {b.kategorie}
                  {b.notiz && <span className="text-ink-muted"> · {b.notiz}</span>}
                </span>
                <span className="block text-xs text-ink-muted">{formatDatum(b.datum)}</span>
              </span>
              <span className={`shrink-0 font-semibold tabular-nums ${ton}`}>
                {einnahme ? '+' : '−'} {formatKurs(betragVon(b))}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/** Tabelle aller Positionen mit Live-Werten und Gewinn/Verlust. */
function PositionenTabelle({ positionen, waehrung, depotWerte, onBearbeiten }) {
  if (positionen.length === 0) {
    return (
      <p className="rounded-2xl border border-dashed border-line bg-paper-soft p-6 text-center text-sm text-ink-muted">
        Noch keine Positionen. Füge z. B. einzelne Wertpapiere, Sparpläne oder Anlagen hinzu.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl border border-line">
      <table className="w-full text-left text-sm">
        <thead className="bg-paper-sunken text-xs uppercase tracking-wide text-ink-muted">
          <tr>
            <th className="px-4 py-3 font-medium">Bezeichnung</th>
            <th className="px-4 py-3 text-right font-medium">Investiert</th>
            <th className="px-4 py-3 text-right font-medium">Aktueller Wert</th>
            <th className="px-4 py-3 text-right font-medium">Gewinn / Verlust</th>
            <th className="px-4 py-3 font-medium">Datum</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {positionen.map((p) => (
            <PositionZeile key={p.id} position={p} waehrung={waehrung} depotWerte={depotWerte} onBearbeiten={() => onBearbeiten(p)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Eine Positionszeile inkl. Depot-Verknüpfung und Gewinn/Verlust. */
function PositionZeile({ position, waehrung, depotWerte, onBearbeiten }) {
  const investiert = positionInvestiert(position);
  const wert = positionWert(position, depotWerte);
  const gv = wert - investiert;
  const gvProz = investiert > 0 ? (gv / investiert) * 100 : null;
  const verknuepft = position.isin ? depotWerte?.[position.isin] : null;
  return (
    <tr className="border-t border-line hover:bg-ink/[0.02]">
      <td className="px-4 py-3">
        <span className="font-medium text-ink">{position.bezeichnung}</span>
        {verknuepft && (
          <span className="ml-2 rounded bg-brand-600/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-600">
            📈 {position.isin}
          </span>
        )}
        {position.notiz && <span className="block text-xs text-ink-muted">{position.notiz}</span>}
      </td>
      <td className="px-4 py-3 text-right tabular-nums text-ink">{formatKurs(investiert, waehrung)}</td>
      <td className="px-4 py-3 text-right tabular-nums text-ink">{formatKurs(wert, waehrung)}</td>
      <td className="px-4 py-3 text-right">
        <GewinnBadge gv={investiert > 0 ? gv : null} gvProz={gvProz} waehrung={waehrung} />
      </td>
      <td className="px-4 py-3 text-ink-soft">{position.datum ? formatDatum(position.datum) : '–'}</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={onBearbeiten}
          className="rounded-lg border border-line px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
        >
          Bearbeiten
        </button>
      </td>
    </tr>
  );
}
